/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { reviewPathName, trackPathName } from "../Constants/constants";

export function getUrlVars(url) {
  const urlVars = {};

  if (url && url.indexOf("?") !== -1) {
    let keyValuePairs = [];
    const index = url.indexOf("?");

    if (url.slice(index + 1)) {
      keyValuePairs = url.slice(index + 1).split("&");
      for (let i = 0; i < keyValuePairs.length; ) {
        const keyValuePair = keyValuePairs[i].split("#")[0];
        const key = keyValuePair.split("=")[0];
        const value = keyValuePair.split("=")[1];

        if (key) {
          urlVars[key] = value;
        }
        i += 1;
      }
    }
  }

  return urlVars;
}

export function getQueryParameterFromUrl(url, paramName) {
  return getUrlVars(window.decodeURIComponent(url))[paramName];
}

export const getUrlWithQueryParameters = (url, queryParams = {}) => {
  let queryParamsString = "";
  const keys = Object.keys(queryParams);
  keys.forEach(key => {
    if (queryParams[key] && queryParams[key].length) {
      queryParamsString += `${key}=${queryParams[key]}&`;
    }
  });

  if (queryParamsString.endsWith("&")) {
    queryParamsString = queryParamsString.substring(
      0,
      queryParamsString.length - 1
    );
  }
  return queryParamsString.length ? `${url}?${queryParamsString}` : url;
};

export const imgBufferToBlob = (buffer, mimeType) =>
  new Blob([buffer], {
    type: mimeType || "img/png"
  });

export function createURLFromBuffer(buffer, mimeType) {
  const blob = imgBufferToBlob(buffer, mimeType);
  return window.URL.createObjectURL(blob);
}

const getOriginFromScriptSrc = src => {
  const anchorTag = document.createElement("a");
  anchorTag.href = src;
  return `${anchorTag.protocol}//${anchorTag.hostname}${
    anchorTag.port ? `:${anchorTag.port}` : ""
  }`;
};

const getScriptSrc = scriptURL => {
  let scriptSrc;
  const scripts = document.scripts;
  [...scripts].forEach(script => {
    const src = script.src;
    if (src.indexOf(scriptURL) !== -1) {
      scriptSrc = src;
      return false;
    }
    return true;
  });

  return scriptSrc;
};

export const isScriptUrlOnSameOrigin = scriptURL => {
  const scriptSrc = getScriptSrc(scriptURL);
  if (scriptSrc) {
    return (
      getOriginFromScriptSrc(window.location.href) ===
      getOriginFromScriptSrc(scriptSrc)
    );
  }
  return false;
};

export const pathExistsInPathName = path => {
  const pathName = window.location.pathname;
  if (pathName.indexOf(path) !== -1) {
    return true;
  }
  return false;
};

export const isSharedFile = () => {
  if (
    pathExistsInPathName(reviewPathName) ||
    pathExistsInPathName(trackPathName)
  ) {
    return true;
  }
  return false;
};
const isUrlEncoded = urlString => decodeURI(urlString) !== urlString;

export const encodeUrlIfNotAlready = urlString =>
  isUrlEncoded(urlString) ? urlString : encodeURI(urlString);

export function crossBrowserURLParse(url, base, global = window) {
  if (global.URL && typeof global.URL === "function") {
    if (base) {
      return new global.URL(url, base);
    }

    return new global.URL(url);
  }

  // IE11
  const parts = [
    "origin",
    "hash",
    "host",
    "hostname",
    "href",
    "password",
    "pathname",
    "port",
    "protocol",
    "search",
    "username"
  ];
  let docElem;

  if (base && typeof base === "string") {
    const urlDoc = global.document.implementation.createHTMLDocument("url");
    const urlBase = urlDoc.createElement("base");
    urlDoc.head.appendChild(urlBase);
    urlBase.href = base;
    docElem = urlDoc;
  } else {
    docElem = document;
  }

  const anchorElem = docElem.createElement("a");
  anchorElem.href = url;

  // When url is relative, anchorElem.href will be resolved relative document.location or base
  // however, host, hostname, etc values are populated as empty.
  // reassigning anchorElem.href with the resolved url fixes this.
  anchorElem.href = anchorElem.href;

  const retObj = {};
  parts.forEach(part => {
    /* istanbul ignore else  */
    if (anchorElem[part] !== undefined) {
      retObj[part] = anchorElem[part];
    } else if (part === "origin" && anchorElem.host && anchorElem.protocol) {
      retObj[part] = `${anchorElem.protocol}//${anchorElem.host}`;
    }
  });
  return retObj;
}

export const getNamePartsFromFullName = name => {
  let fileName = name;
  let fileExtension = "";
  if (name) {
    const i = name.lastIndexOf(".");
    if (i !== -1) {
      fileName = name.slice(0, i);
      fileExtension = name.slice(i + 1);
    }
  }
  return { fileName, fileExtension };
};
